import React, { useState, useEffect } from "react";
import axios from "../common/axios";
import { Link as RouterLink, useNavigate, useLocation } from "react-router-dom";
import { EyeOff } from "lucide-react";
import { Button } from "src/components/ui/button";
import { Input } from "src/components/ui/input";
import { Label } from "src/components/ui/label";

export default function NewPW() {
  const [password, setPassword] = useState("");
  const [confirmpassword, setConfirmPassword] = useState("");
  const [strength, setStrength] = useState(0);
  const [message, setMessage] = useState("");
  const [isValid, setIsValid] = useState(true);
  // const history = useHistory();
  const navigate = useNavigate(); // useNavigate 사용

  const search = useLocation().search;
  const token = new URLSearchParams(search).get("token");
  const username = new URLSearchParams(search).get("username");

  useEffect(() => {
    if (password === "") {
      setIsValid(true);
    } else if (strength !== 5) {
      setIsValid(true);
    } else if (confirmpassword === "") {
      setIsValid(true);
    } else if (password !== confirmpassword) {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
  }, [confirmpassword, password, strength]);

  // useEffect(() => {
  // 	setIsValid(strength ===5?false:true)
  //   },[strength])

  function isValidPassword(password) {
    const upperCaseRegex = /[A-Z]/;
    const lowerCaseRegex = /[a-z]/;
    const numberRegex = /\d/;
    const specialCharRegex = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;

    const checks = [
      upperCaseRegex.test(password),
      lowerCaseRegex.test(password),
      numberRegex.test(password),
      specialCharRegex.test(password),
    ];

    const trueCounts = checks.filter(Boolean).length;

    if (trueCounts >= 3 && password.length >= 8) return true;
    if (trueCounts >= 2 && password.length >= 10) return true;

    return false;
  }

  // 제약 조건을 검사하는 Helper 함수들
  const hasSequentialCharacters = (str) =>
    /(012|123|234|345|456|567|678|789|890|abc|bcd|cde|def|efg|fgh|ghi|hij|ijk|jkl|klm|lmn|mno|nop|opq|pqr|qrs|rst|stu|tuv|uvw|vwx|wxy|xyz|qwe|wer|ert|rty|tyu|yui|uio|iop|asd|sdf|dfg|fgh|ghj|hjk|jkl|zxc|xcv|cvb|vbn|bnm)/i.test(
      str
    );

  const hasRepeatedCharacters = (str) => /(.)\1\1/.test(str);

  const isOnlyNumericOrAlpha = (str) => /^[0-9]+$|^[a-zA-Z]+$/.test(str);

  const validatePassword = (passwordValue) => {
    if (passwordValue.includes(username))
      return "패스워드가 사용자 계정과 동일합니다."; // 1번 제약 조건
    if (hasRepeatedCharacters(passwordValue))
      return "동일한 문자 또는 숫자가 3자 이상 사용되었습니다."; // 2번 제약 조건
    if (hasSequentialCharacters(passwordValue))
      return "주기성 문자나 키보드상의 연속된 배열이 3개 이상 사용되었습니다."; // 3번 제약 조건
    // 4번 제약 조건은 추가 정보(전화번호, 생년월일 등)가 필요하여 여기서 처리하지 않음
    if (isOnlyNumericOrAlpha(passwordValue))
      return "문자 또는 숫자만으로 구성된 패스워드는 허용되지 않습니다."; // 6번 제약 조건
    if (passwordValue === "") return "Null 패스워드는 허용되지 않습니다."; // 7번 제약 조건

    if (!isValidPassword(passwordValue)) {
      return "패스워드는 다음의 문자 종류 중 2종류 이상을 조합하여 최소 10자리 이상 또는 3종류 이상을 조합하여 최소 8자리 이상의 길이로 작성하여야 합니다. * 영문 대문자(26개), 영문 소문자(26개), 숫자(10개), 특수문자(32개)";
    }
    return null; // 유효한 패스워드
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    const validationResult = validatePassword(newPassword);

    if (validationResult === null) {
      setMessage("사용가능한 비밀번호입니다.");
    } else {
      setMessage(validationResult);
    }
  };

  const handleConfirmPasswordChange = (e) => {
    const newPassword = e.target.value;
    setConfirmPassword(newPassword);

    if (password === newPassword) {
      setMessage("비밀번호가 서로 일치합니다.");
    } else {
      setMessage("비밀번호가 서로 일치하지 않습니다.");
    }
  };

  function getPasswordStrength(password) {
    let score = 0;
    const consecutivePattern = /(.)\1\1+/; // 3번 이상 연속되는 문자

    if (password.length >= 10) score++;
    if (/[^a-zA-Z0-9]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (consecutivePattern.test(password)) score = 0; // 연속된 문자가 있다면 점수를 0으로 초기화

    return score;
  }

  useEffect(() => {
    setStrength(getPasswordStrength(password));
  }, [password]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`/loa_new_password`, {
        token,
        password,
      });
      // 성공 응답 처리
      if (response.data.success) {
        setMessage(response.data.message); // 성공 메시지 설정
        navigate("/password-confirmation");
      } else {
        // 예상치 못한 응답 처리
        setMessage(
          "비밀번호 재설정에 문제가 발생했습니다. 시스템 관리자에게 연락하시기 바랍니다."
        );
      }
    } catch (err) {
      console.log(err);
      if (err.response && err.response.data && err.response.data.error) {
        // 서버에서 반환하는 에러 메시지를 사용자에게 보여줍니다.
        setMessage(err.response.data.error);
      } else {
        setMessage(
          "비밀번호 재설정에 문제가 발생했습니다. 시스템 관리자에게 연락하시기 바랍니다."
        );
      }
    }
  };

  return (
    <>
      <div className="flex min-h-screen flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-[336px]">
          <div className="text-center mb-7">
            <div className="flex items-center justify-center mb-4">
              <a href="http://www.samsungshi.com/kor/default.aspx">
                <img alt="Logo" src="/images/g1313.png" className="h-8 w-auto" />
              </a>
            </div>
            <h1 className="text-foreground font-extrabold text-[19px] tracking-tight mb-2">
              삼성중공업 현장공무 LOA 결재 시스템
            </h1>
            <p className="text-muted-foreground text-sm">
              이미 비밀번호를 새로 설정하셨나요?{" "}
              <span
                className="text-primary font-semibold cursor-pointer hover:underline"
                onClick={() => navigate("/login")}
              >
                로그인
              </span>
            </p>
          </div>

          <div className="rounded-lg border bg-card p-6">
            <h2 className="text-foreground font-bold text-base mb-1">
              새 비밀번호 설정
            </h2>
            <p className="text-muted-foreground text-xs mb-5">
              새로 사용할 비밀번호를 입력하세요.
            </p>

            <form noValidate>
              <div className="mb-3.5">
                <Label htmlFor="password" className="mb-1.5 block">
                  비밀번호
                </Label>
                <div className="relative mb-2">
                  <Input
                    id="password"
                    type="password"
                    placeholder="비밀번호"
                    name="password"
                    autoComplete="off"
                    onChange={handlePasswordChange}
                  />
                  <span className="absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground">
                    <EyeOff size={18} />
                  </span>
                </div>
                <div className="flex items-center mb-2">
                  <div
                    className={`flex-1 ${
                      strength > 0 ? "bg-green-500" : "bg-muted"
                    } rounded h-[5px] mr-2`}
                  ></div>
                  <div
                    className={`flex-1 ${
                      strength > 1 ? "bg-green-500" : "bg-muted"
                    } rounded h-[5px] mr-2`}
                  ></div>
                  <div
                    className={`flex-1 ${
                      strength > 2 ? "bg-green-500" : "bg-muted"
                    } rounded h-[5px] mr-2`}
                  ></div>
                  <div
                    className={`flex-1 ${
                      strength > 3 ? "bg-green-500" : "bg-muted"
                    } rounded h-[5px] mr-2`}
                  ></div>
                  <div
                    className={`flex-1 ${
                      strength > 4 ? "bg-green-500" : "bg-muted"
                    } rounded h-[5px]`}
                  ></div>
                </div>
                <p className="text-muted-foreground text-xs">
                  최소 10글자 이상의 비밀번호를 사용해주세요. 비밀번호에는
                  대문자, 소문자, 특수문자 등이 포함되어야하며, 3개 이상의
                  연속된 문자를 사용할 수 없습니다.
                </p>
              </div>
              <div className="mb-3.5">
                <Label htmlFor="confirm-password" className="mb-1.5 block">
                  비밀번호 확인
                </Label>
                <Input
                  id="confirm-password"
                  type="password"
                  placeholder="비밀번호 재입력"
                  name="confirm-password"
                  autoComplete="off"
                  onChange={handleConfirmPasswordChange}
                />
              </div>
              <div className="mb-4">
                <span className="font-semibold text-muted-foreground text-xs">
                  {message}
                </span>
              </div>
              <Button
                type="button"
                disabled={isValid}
                onClick={handleSubmit}
                className="w-full"
              >
                제출
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
